import { useEffect, useState } from "react";
import getSubUserDetails from "./helpers/getSubUserDetails";
import Icon from "../../../../../../Elements/Icon/Icon";
import bytesToDisplayHelper from "../../../../../../../utils/bytesToDisplayHelper";
import { useUser } from "../../../../../../../contexts/userContext/userContext";
import type { UserDataT } from "../../../../../../../models/models";
import Tooltip from "../../../../../../Elements/Tooltip/Tooltip";
import { useAppSelector } from "../../../../../../../store/hooks";
import { selectSearchParams } from "../../../../../../../store/selectors/inboxSelectors";

import styles from "./styles.module.scss";

type SubUserDetailsT = {
  Email: string;
  Name: string;
  Storage: number;
};

/**
 * Component that displays the storage information for a selected sub user
 * if only one sub user is selected.
 */

const SelectedSubUserData = () => {
  const { getUserData } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const searchParams = useAppSelector(selectSearchParams);
  const selectedSubUser = searchParams?.su?.[0];

  // State to store the selected sub user data
  const [subUserData, setSubUserData] = useState<SubUserDetailsT>();

  // Get top user data
  const userData = getUserData() as UserDataT;

  // Get the user storage amount and the sub user storage used
  const userStorageAmount = userData.UserMaxStorage;
  const userStorageFormatted = bytesToDisplayHelper(
    userStorageAmount || 0,
    false
  );
  const subUserStorageUsed = bytesToDisplayHelper(
    subUserData?.Storage || 0,
    false
  );
  // Calculate the sub user storage used percentage
  const subUserStorageUsedPercentage =
    ((subUserData?.Storage || 0) / userStorageAmount) * 100;

  // If the subUserEmailList has a length of 1, get the sub user details
  useEffect(() => {
    async function fetchData() {
      if (selectedSubUser) {
        setIsLoading(true);

        // Get the sub user details
        const userData = (await getSubUserDetails(
          selectedSubUser
        )) as SubUserDetailsT;

        setSubUserData(userData);
        setIsLoading(false);
      } else {
        setSubUserData(undefined);
        setIsLoading(false);
      }
    }

    fetchData();
  }, [selectedSubUser]);

  // If the sub user data exists, display the storage information
  if (subUserData && !isLoading) {
    return (
      //@ts-ignore
      <Tooltip tooltipTextKey="sub-user-used-storage-tooltip">
        <div className={styles.selectedSubUserDataWrapper}>
          <Icon
            name="newinbox/storage-cloud"
            showTitle={false}
            iconClass={styles.cloudIcon}
          />
          <span>
            {subUserStorageUsed.size}
            {subUserStorageUsed.type}/{userStorageFormatted.size}
            {userStorageFormatted.type} (
            {subUserStorageUsedPercentage.toFixed(2)}
            %)
          </span>
        </div>
      </Tooltip>
    );
  } else {
    return null;
  }
};

export default SelectedSubUserData;
